import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Building, Users, Layers, FileEdit, Mail, Briefcase, Home, KeyRound, CreditCard, AlertTriangle, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shell } from "@/components/Shell";
import { PageHeader, Loading, StatCard, StatusBadge } from "@/components/common";
import { AccountCard } from "@/components/AccountCard";
import { api, useFetch, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminPlans from "@/pages/admin/AdminPlans";
import { AdminSite, AdminSmtp } from "@/pages/admin/AdminSettings";

function Overview() {
  const { data } = useFetch("/admin/stats");
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-overview-page">
      <PageHeader eyebrow="Super admin" title="Platform overview" subtitle="Everything happening across all businesses." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Businesses" value={data.businesses} icon={Building} testid="admin-stat-businesses" />
        <StatCard label="Letting agents" value={data.agents} icon={Briefcase} testid="admin-stat-agents" />
        <StatCard label="Landlords" value={data.landlords} icon={Home} testid="admin-stat-landlords" />
        <StatCard label="Paying subscribers" value={data.paying} icon={CreditCard} tone="green" testid="admin-stat-paying" />
        <StatCard label="Properties" value={data.properties} icon={Building} testid="admin-stat-properties" />
        <StatCard label="Active tenancies" value={data.tenancies} icon={KeyRound} testid="admin-stat-tenancies" />
        <StatCard label="Renters" value={data.renters} icon={Users} testid="admin-stat-renters" />
        <StatCard label="Failed emails" value={data.emails_failed} icon={AlertTriangle} tone={data.emails_failed ? "red" : "slate"} testid="admin-stat-emails-failed" />
      </div>
    </div>
  );
}

function Businesses() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const loginAs = async (b) => {
    if (!window.confirm(`Log in as ${b.name}? You'll see their account exactly as they do. Use "Return to admin" to come back.`)) return;
    try {
      await api.post(`/admin/businesses/${b.id}/impersonate`);
      await refresh();
      nav("/app");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const { data, reload } = useFetch("/admin/businesses");
  const { data: plans } = useFetch("/admin/plans");
  const patch = async (b, body) => {
    try { await api.patch(`/admin/businesses/${b.id}`, body); toast.success("Updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-businesses-page">
      <PageHeader eyebrow="Customers" title="Businesses" subtitle="Landlords and letting agents using the platform." />
      {!data ? <Loading /> : (
        <div className="panel p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Business</TableHead><TableHead>Type</TableHead><TableHead>Properties</TableHead><TableHead>Plan</TableHead><TableHead>Subscription</TableHead><TableHead>Joined</TableHead><TableHead>Active</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.map((b) => (
                <TableRow key={b.id} data-testid={`admin-biz-row-${b.id}`}>
                  <TableCell className="font-semibold">{b.name}<div className="text-xs text-slate-500 font-normal">{b.email}</div></TableCell>
                  <TableCell className="capitalize">{b.type}</TableCell>
                  <TableCell>{b.property_count}</TableCell>
                  <TableCell>
                    <Select value={b.plan_id || undefined} onValueChange={(v) => patch(b, { plan_id: v })}>
                      <SelectTrigger className="w-40 h-8" data-testid={`admin-biz-plan-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{(plans || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><StatusBadge status={b.subscription_status} /></TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(b.created_at)}</TableCell>
                  <TableCell><Switch checked={b.status !== "suspended"} onCheckedChange={(c) => patch(b, { status: c ? "active" : "suspended" })} data-testid={`admin-biz-active-${b.id}`} /></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => loginAs(b)} disabled={b.status === "suspended"} data-testid={`admin-biz-login-as-${b.id}`}><LogIn className="w-4 h-4 mr-1" />Log in as</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function UsersPage() {
  const { session } = useAuth();
  const { data, reload } = useFetch("/admin/users");
  const toggle = async (u, enabled) => {
    try { await api.patch(`/admin/users/${u.id}`, { disabled: !enabled }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-users-page">
      <PageHeader eyebrow="Accounts" title="Users" subtitle="Every login on the platform: admins, business users and renters." />
      {!data ? <Loading /> : (
        <div className="panel p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Business</TableHead><TableHead>Created</TableHead><TableHead>Enabled</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><StatusBadge status={u.role} label={u.role.replace("_", " ")} /></TableCell>
                  <TableCell className="text-sm">{u.business_name}</TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(u.created_at)}</TableCell>
                  <TableCell><Switch disabled={u.id === session.user.id} checked={!u.disabled} onCheckedChange={(c) => toggle(u, c)} data-testid={`admin-user-enabled-${u.id}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AdminApp() {
  const items = [
    { id: "admin-overview", to: "/admin", end: true, label: "Overview", icon: LayoutDashboard },
    { id: "admin-businesses", to: "/admin/businesses", label: "Businesses", icon: Building },
    { id: "admin-users", to: "/admin/users", label: "Users", icon: Users },
    { id: "admin-plans", to: "/admin/plans", label: "Plans", icon: Layers },
    { id: "admin-site", to: "/admin/site", label: "Site content", icon: FileEdit },
    { id: "admin-smtp", to: "/admin/smtp", label: "Email & SMTP", icon: Mail },
    { id: "admin-account", to: "/admin/account", label: "My account", icon: KeyRound },
  ];
  return (
    <Shell items={items} title="Platform administration" subtitle="Super admin">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="site" element={<AdminSite />} />
        <Route path="smtp" element={<AdminSmtp />} />
        <Route path="account" element={<div><PageHeader eyebrow="Account" title="My account" /><AccountCard /></div>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Shell>
  );
}
